import { UniqueIdentifier } from "@dnd-kit/core";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useState } from "react";
import { twMerge } from "tailwind-merge";

import { CrossIcon, DragIcon } from "ui/icon/shared";
import { ScoreBadge } from "ui/widget/badge";

type SymbolItemProps = {
  symbol: string;
  companyName: string;
  score: number;
  activeId: UniqueIdentifier | null;
  onSymbolClick: (symbol: string) => void;
  onRemoveClick: (symbol: string) => void;
  onScoreClick?: (symbol: string, score?: number) => void;
};

export const SymbolItem = ({
  symbol,
  companyName,
  score,
  activeId,
  onSymbolClick,
  onRemoveClick,
}: SymbolItemProps) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: symbol });
  const style = { transform: CSS.Transform.toString(transform), transition };
  const [isHover, setIsHover] = useState(false);

  return (
    <div
      aria-label={`watchlist item ${symbol}`}
      ref={setNodeRef}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
      style={style}
      {...attributes}
      {...listeners}
      className={twMerge(
        "relative flex items-center gap-2 bg-white px-8 py-2 focus:outline-none",
        activeId === symbol && "z-10 shadow-md"
      )}
      onClick={(e) => {
        e.stopPropagation();
        onSymbolClick(symbol);
      }}
    >
      {/* Hover Icon */}
      {isHover && (
        <span className="absolute left-1 top-1/2 flex -translate-y-1/2 cursor-pointer items-center justify-center rounded-md p-1 transition duration-100 hover:bg-gray-300/30 hover:shadow-sm">
          <DragIcon />
        </span>
      )}

      {/* Delete Icon */}
      {isHover && (
        <div
          role="button"
          aria-label={`remove ${symbol}`}
          className="absolute right-1 top-1/2 flex -translate-y-1/2 cursor-pointer items-center justify-center rounded-md p-1 transition duration-100 hover:bg-gray-300/30 hover:shadow-sm"
          onClick={(e) => {
            e.stopPropagation();
            onRemoveClick(symbol);
          }}
        >
          <CrossIcon className="scale-[0.6] fill-zinc-400" />
        </div>
      )}

      {/* Item */}
      <div className="flex w-full items-center justify-between gap-2 overflow-hidden">
        <div className="w-full overflow-hidden text-start">
          <p className="text-sm font-medium">{symbol}</p>
          <p className="truncate text-sm text-zinc-500">{companyName}</p>
        </div>
        <ScoreBadge
          role="button"
          score={score}
          aria-label={`navigate to ${symbol} score page`}
          className="shrink-0"
        />
      </div>
    </div>
  );
};
